import { SagaIterator } from 'redux-saga';
import { call, put,takeEvery } from 'redux-saga/effects';

import { ResourceGovernanceEditSite, ResourceGovernanceResetSiteToDefaults } from '~src/app/actions/AppActionTypes';
import { requestFailed } from '~src/app/actions/RequestFailedAction';
import { requestSpaceContents } from '~src/app/actions/spaces/RequestSpaceContentsActions';
import { Messages } from '~src/codegen/Localize';
import { openReactConfirmationDialog } from '~src/commonInfra/dialogs/ReactConfirmationDialog';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { WebHttpClientWithErrorToastInstance } from '~src/httpClient/WebHttpClientWithErrorToast';
import { ToasterService, ToastType } from '~src/ts/services/ToasterService';

import { ResourceGovernanceEditSiteAction } from '../actions/ResourceGovernanceEditSiteAction';
import { ResourceGovernanceResetSiteToDefaultsAction } from '../actions/ResourceGovernanceResetSiteToDefaultAction';
import { openResourceGovernanceEditSiteLimitsDialog, ResourceGovernanceEditSiteLimitsDialogProps } from '../components/ResourceGovernanceEditSiteLimitsDialog';
import { GetResourceGovernanceServerSettingsRequest, IGetResourceGovernanceServerSettingsResult, IResourceGovernanceSiteSettingUpdate,UpdateResourceGovernanceSiteSettingsRequest } from '../types/ResourceGovernance';

export function* editSiteLimit(action: ResourceGovernanceEditSiteAction) {
  try{
    let defaultLimits: IGetResourceGovernanceServerSettingsResult = yield call(getGovernanceServerSettings);
    if (defaultLimits && defaultLimits.errors?.length){
      yield put(requestFailed('Failed to get default limits', Messages.errorUnknown()));
      return;
    }
    let props: ResourceGovernanceEditSiteLimitsDialogProps = {
      sitesSettingsList: action.settings,
      serverSettings: defaultLimits.governanceServerSettings,
    };
    yield call(openResourceGovernanceEditSiteLimitsDialog, props);
    yield put (requestSpaceContents());
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export async function openResetSiteLimitDialog(siteIds: string[]): Promise<IResult> {
  const dialog = openReactConfirmationDialog({
    title: Messages.resourceGovernanceResetSiteToDefaultsTitle(),
    message: Messages.resourceGovernanceResetSiteToDefaultsMessage(),
    actionName: Messages.confirm(),
    action: siteIds => {
      const updateParams: IResourceGovernanceSiteSettingUpdate = {
        siteIds,
        isDefault: true,
        // None of the other fields matter
        extractsConcurrencyLimit: null,
        subscriptionsConcurrencyLimit: null,
        flowsConcurrencyLimit: null,
        runtimeLimit: null,
      };
      return WebHttpClientWithErrorToastInstance.sendRequest(
        new UpdateResourceGovernanceSiteSettingsRequest(updateParams));
    },
    paramsForAction: siteIds,
    dialogWidth: 500,
  });
  const result = await dialog.result;
  if (result.errors?.length > 0) {
    ToasterService.getInstance().pop({
      type: ToastType.Error,
      message: Messages.resourceGovernanceResetSiteToDefaultsError(),
      actionMessage: Messages.learnMore(),
      actionHref: 'http://example.com',
    });
  } else {
    ToasterService.getInstance().pop({
      type: ToastType.Success,
      message: Messages.resourceGovernanceResetSiteToDefaultsSuccess(),
    });
  }
  return result;
}

export function* resetSiteLimit(action: ResourceGovernanceResetSiteToDefaultsAction) {
  try {
    const confirmDialog: IResult = yield call(openResetSiteLimitDialog, action.siteIds);
  } catch (e) {
    yield put(requestFailed(e, Messages.errorUnknown()));
  }
}

export function* rootResourceGovernanceSaga(): SagaIterator {
  yield takeEvery(ResourceGovernanceEditSite, editSiteLimit);
  yield takeEvery(ResourceGovernanceResetSiteToDefaults, resetSiteLimit);
}

export function getGovernanceServerSettings(): Promise<IGetResourceGovernanceServerSettingsResult>{
  return WebHttpClientWithErrorToastInstance.sendRequest(
    new GetResourceGovernanceServerSettingsRequest());
}
